import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, X, Download, FileText, ExternalLink, ChevronDown, ChevronRight } from 'lucide-react';
import {
	getGstSummary,
	getGstMeta,
	getGstVendors,
	getGstVendorHeader,
	getGstPurchases,
	getGstSales,
	getGstPurchaseDetail,
	getGstSalesDetail,
	getGstPurchaseAttachment,
	getGstSalesAttachment,
	exportGstPurchases,
	exportGstSales,
	updateGstFindings,
	inr,
	inrFull,
} from '../data';
import type { GstAttachment } from '../data';
import { VendorFigures, VendorTabTable } from './VendorDrillDown';
import { PageSizeSelect } from './PageSize';
import { Dropdown } from '../../../components/ui/Dropdown';
import { causeLabel } from '../causeLabels';
import { exportGstCausesWorkbook } from '../exportCauses';
import type {
	GstSummary,
	GstMeta,
	GstTabInfo,
	GstPurchaseRow,
	GstSalesRow,
	GstPurchaseDetail,
	GstSalesDetail,
	GstPagination,
	GstMismatchTag,
	GstTotals,
	GstCategoryCount,
	GstFinding,
	GstFindingDetail,
	GstVendor,
	GstVendorHeader,
	GstFindingStatus,
	GstRowAction,
} from '../types';

const MONTH_NAMES = [
	'Jan',
	'Feb',
	'Mar',
	'Apr',
	'May',
	'Jun',
	'Jul',
	'Aug',
	'Sep',
	'Oct',
	'Nov',
	'Dec',
];

/** String-slice date formatting — never `new Date(x)` here. Zoho dates arrive as
 *  IST-midnight stored as the previous day's 18:30 UTC; parsing through Date() in a
 *  browser can silently shift the displayed day depending on local timezone. */
const shortDate = (d?: string | null) => {
	if (!d) return '—';
	const [y, m, day] = d.slice(0, 10).split('-');
	if (!y || !m || !day) return d;
	return `${day} ${MONTH_NAMES[Number(m) - 1] ?? m} ${y.slice(-2)}`;
};

/** '2025-04' → 'Apr 2025'. Same string-slice rule as shortDate: no Date() anywhere near a
 *  period, or a browser west of UTC renders April as March. */
const monthLabel = (p: string) => {
	const [y, m] = p.split('-');
	return `${MONTH_NAMES[Number(m) - 1] ?? m} ${y}`;
};

type Tone = 'emerald' | 'amber' | 'rose' | 'slate';

const Tag: React.FC<{ tone: Tone; children: React.ReactNode; title?: string }> = ({
	tone,
	children,
	title,
}) => {
	const cls: Record<Tone, string> = {
		emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
		amber: 'bg-amber-50 text-amber-700 border-amber-200',
		rose: 'bg-rose-50 text-rose-700 border-rose-200',
		slate: 'bg-slate-100 text-slate-600 border-slate-200',
	};
	return (
		<span
			title={title}
			className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[11px] font-medium ${cls[tone]}`}>
			{children}
		</span>
	);
};

/** Colour language for each status, shared across both sides. rose = real cash/compliance
 *  risk, amber = a coverage gap worth a look, slate = informational or explicitly not a
 *  problem, emerald = clean match. */

/** Only `reviewed_ok` has actually been signed off by accounts — it is the only status the audit
 *  bridge deducts from the reported gap. Everything else is still open, in different ways. */
const FINDING_STATUS_LABEL: Record<string, string> = {
	needs_review: 'Needs review',
	in_progress: 'In progress',
	reviewed_ok: 'Signed off',
	wont_fix: "Won't fix",
	blocked: 'Blocked',
};
const findingStatusTone = (st?: string | null) =>
	st === 'reviewed_ok'
		? 'bg-emerald-50 text-emerald-700 border-emerald-200'
		: st === 'blocked'
		? 'bg-rose-50 text-rose-700 border-rose-200'
		: st === 'wont_fix'
		? 'bg-slate-100 text-slate-500 border-slate-200'
		: 'bg-amber-50 text-amber-700 border-amber-200';

/**
 * The cause breakdown for IN_2B_NOT_IN_ZOHO — the report and the filter in one control.
 *
 * WHY IT IS A PANEL AND NOT A DROPDOWN. This IS the answer the auditor asks for: what is the
 * ₹3.96 Cr actually made of. Hiding it behind a dropdown would mean the one number everyone wants
 * is never on screen. It collapses once they have picked a cause, so it costs vertical space only
 * while it is being used.
 *
 * ONLY RENDERED FOR IN_2B_NOT_IN_ZOHO. The other four buckets have no findings at all, so the
 * panel would be an empty box promising information that does not exist.
 */
const CauseBreakdown: React.FC<{
	rows: GstCategoryCount[];
	value: string;
	onPick: (category: string) => void;
	open: boolean;
	onToggle: () => void;
}> = ({ rows, value, onPick, open, onToggle }) => {
	const total = rows.reduce((t, r) => t + r.count, 0);
	const totalValue = rows.reduce((t, r) => t + r.value_rs, 0);
	// Biggest money first: the accountant's next action is always the largest unexplained pile.
	const sorted = [...rows].sort((a, b) => b.value_rs - a.value_rs);

	return (
		<div className='mb-3 rounded-lg border border-gray-200 bg-gray-50/60'>
			<button
				onClick={onToggle}
				className='flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-medium text-gray-700 hover:bg-gray-100/60'>
				{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
				Why are these {total.toLocaleString()} unmatched?
				<span className='font-normal text-gray-400'>
					{sorted.length} causes · {inrFull(totalValue)}
				</span>
				{value && (
					<span className='ml-auto rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700'>
						{causeLabel(value)} ✕
					</span>
				)}
			</button>

			{open && (
				<div className='border-t border-gray-200 px-1.5 pb-1.5'>
					<table className='w-full text-xs'>
						<tbody>
							<tr
								onClick={() => onPick('')}
								className={`cursor-pointer ${!value ? 'font-medium text-blue-700' : 'text-gray-600 hover:bg-white'}`}>
								<td className='py-1.5 pl-2'>All causes</td>
								<td className='py-1.5 pr-3 text-right tabular-nums'>{total.toLocaleString()}</td>
								<td className='py-1.5 pr-2 text-right font-mono'>{inrFull(totalValue)}</td>
							</tr>
							{sorted.map(c => (
								<tr
									key={c.category}
									onClick={() => onPick(c.category === value ? '' : c.category)}
									className={`cursor-pointer border-t border-gray-200/70 ${
										c.category === value ? 'bg-blue-50 font-medium text-blue-700' : 'text-gray-600 hover:bg-white'
									}`}>
									<td className='py-1.5 pl-2'>
										{causeLabel(c.category)}
										{/* Signed off = accounts has accepted it and the bridge has deducted it.
										    A cause usually holds several vendors at different stages, so say HOW
										    MANY — "signed off" on the whole row would claim 33 rows are closed
										    when 9 are. Only when every row is signed off does it read as done. */}
										{c.signed_off > 0 && (
											<span className='ml-1.5 rounded border border-emerald-200 bg-emerald-50 px-1 py-px text-[10px] font-normal text-emerald-700'>
												{c.signed_off === c.count ? 'signed off' : `${c.signed_off} of ${c.count} signed off`}
											</span>
										)}
										{c.category === 'none' && (
											<span className='ml-1.5 text-[10px] font-normal text-gray-400'>nobody has looked yet</span>
										)}
									</td>
									<td className='py-1.5 pr-3 text-right tabular-nums'>{c.count.toLocaleString()}</td>
									<td className='py-1.5 pr-2 text-right font-mono'>{inrFull(c.value_rs)}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
};

/**
 * The investigation, on the detail drawer.
 *
 * WHAT THIS REPLACED. The drawer used to open with one sentence — "The vendor filed this invoice
 * in their GSTR-1 but there is no matching bill in Zoho" — which is true of all 1,428 rows in the
 * bucket and therefore tells the reader nothing about the row they just clicked. Everything the
 * investigation found was sitting in the database, unread.
 *
 * ORDER MATTERS, AND IT IS DELIBERATE:
 *   1. this invoice's own fact, if one was recorded  (most specific — can contradict the vendor line)
 *   2. plain English about the supplier              (what is actually wrong)
 *   3. the full working                              (for the auditor, collapsed)
 *   4. the documents cited                           (proof, collapsed)
 * A reader who only ever reads line 1 should still be correctly informed.
 */
const FindingPanel: React.FC<{ finding: GstFindingDetail }> = ({ finding: f }) => {
	const [showNote, setShowNote] = useState(false);
	const [showEvidence, setShowEvidence] = useState(false);
	const money = (n: number | string | null) =>
		n === null || n === undefined ? '—' : inrFull(Number(n));

	return (
		<section className='rounded-lg border border-blue-200 bg-blue-50/40 p-4'>
			<div className='mb-2 flex flex-wrap items-center gap-2'>
				<span className='text-sm font-semibold text-gray-800'>{causeLabel(f.category)}</span>
				<span
					className={`rounded border px-1.5 py-0.5 text-[11px] font-medium ${findingStatusTone(f.status)}`}>
					{FINDING_STATUS_LABEL[f.status] ?? f.status}
				</span>
				{f.reported_rs !== null && (
					<span className='text-xs text-gray-500'>
						vendor total {money(f.reported_rs)}
						{!!f.explained_rs && f.explained_rs > 0 && (
							<span className='text-emerald-700'> · {money(f.explained_rs)} explained</span>
						)}
					</span>
				)}
			</div>

			{/* 1. This invoice specifically. Shown first because a vendor-level sentence can be
			    actively wrong about one of its rows — "suspected typo" against an ₹800 invoice. */}
			{f.row_summary && (
				<p className='mb-2 rounded border border-amber-200 bg-amber-50 p-2 text-sm text-gray-800'>
					<span className='font-medium'>This invoice: </span>
					{f.row_summary}
				</p>
			)}

			{/* 2. The supplier's situation, in plain English. */}
			{f.plain_summary ? (
				<p className='text-sm leading-relaxed text-gray-700'>{f.plain_summary}</p>
			) : (
				<p className='text-sm italic text-gray-500'>
					No plain-English summary written for this finding yet — the full working is below.
				</p>
			)}

			<div className='mt-3 flex flex-wrap gap-3 text-xs'>
				{f.note && (
					<button
						onClick={() => setShowNote(v => !v)}
						className='flex items-center gap-1 font-medium text-blue-700 hover:underline'>
						{showNote ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
						Full working
					</button>
				)}
				{f.evidence.length > 0 && (
					<button
						onClick={() => setShowEvidence(v => !v)}
						className='flex items-center gap-1 font-medium text-blue-700 hover:underline'>
						{showEvidence ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
						{f.evidence.length} documents cited
					</button>
				)}
			</div>

			{/* 3. The working, exactly as the investigator wrote it — whitespace preserved, because
			    these notes are laid out as tables and step lists and reflow destroys them. */}
			{showNote && f.note && (
				<pre className='mt-2 max-h-80 overflow-auto whitespace-pre-wrap rounded border border-gray-200 bg-white p-3 font-sans text-xs leading-relaxed text-gray-700'>
					{f.note}
				</pre>
			)}

			{/* 4. The proof. effect tells the reader what each document DID: 'target'/'reduce'
			    removed money from the gap, 'open' is still unexplained, 'proof' is an email or
			    attachment cited as evidence rather than an amount. */}
			{showEvidence && f.evidence.length > 0 && (
				<div className='mt-2 max-h-80 overflow-auto rounded border border-gray-200 bg-white'>
					<table className='w-full text-xs'>
						<thead className='sticky top-0 bg-gray-50 text-[11px] uppercase text-gray-500'>
							<tr>
								<th className='py-1.5 pl-2 text-left'>Document</th>
								<th className='py-1.5 pr-2 text-left'>Date</th>
								<th className='py-1.5 pr-2 text-right'>Amount</th>
								<th className='py-1.5 pr-2 text-left'>Effect</th>
							</tr>
						</thead>
						<tbody>
							{f.evidence.map((e, i) => (
								<tr key={`${e.source_table}-${e.source_id}-${i}`} className='border-t border-gray-100 align-top'>
									<td className='py-1.5 pl-2'>
										<div className='font-mono text-[11px]'>{e.document_number || e.source_table}</div>
										{e.summary && <div className='mt-0.5 text-[11px] text-gray-500'>{e.summary}</div>}
									</td>
									<td className='py-1.5 pr-2 whitespace-nowrap text-gray-500'>{e.document_date || '—'}</td>
									<td className='py-1.5 pr-2 text-right font-mono whitespace-nowrap'>
										{Number(e.amount_rs) ? money(e.amount_rs) : '—'}
									</td>
									<td className='py-1.5 pr-2'>
										<span
											className={`rounded px-1 py-px text-[10px] ${
												e.effect === 'open'
													? 'bg-amber-50 text-amber-700'
													: e.effect === 'proof'
													? 'bg-slate-100 text-slate-600'
													: 'bg-emerald-50 text-emerald-700'
											}`}>
											{e.effect}
										</span>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</section>
	);
};

const STATUS_TONE: Record<string, Tone> = {
	MATCHED: 'emerald',
	MATCHED_AMOUNT_MISMATCH: 'amber',
	IN_2B_NOT_IN_ZOHO: 'slate',
	IN_ZOHO_NOT_IN_2B: 'rose',
	ZOHO_BILL_NO_GSTIN: 'amber',
	IN_GSTR1_NOT_IN_ZOHO: 'slate',
	IN_ZOHO_NOT_IN_GSTR1: 'rose',
	VOID_OR_DRAFT_CORRECTLY_NOT_IN_GSTR1: 'slate',
};

/** Chip colour per mismatch tag — green/neutral = resolved, red = needs a human look. */
const TAG_TONE: Record<GstMismatchTag, Tone> = {
	TDS_CONFIRMED: 'emerald',
	TDS_LIKELY: 'amber',
	ENTIRE_GST_MISSING_IN_ZOHO: 'amber',
	BASE_AMOUNT_MISMATCH: 'rose',
	ZOHO_HIGHER_THAN_2B: 'rose',
	UNEXPLAINED: 'rose',
};
const TAG_LABEL: Record<GstMismatchTag, string> = {
	TDS_CONFIRMED: 'TDS confirmed',
	TDS_LIKELY: 'TDS likely',
	ENTIRE_GST_MISSING_IN_ZOHO: 'GST missing in Zoho',
	BASE_AMOUNT_MISMATCH: 'Base amount mismatch',
	ZOHO_HIGHER_THAN_2B: 'Zoho higher than 2B',
	UNEXPLAINED: 'Unexplained',
};

const tabLabel = (t: GstTabInfo) => t.label || t.status.replace(/_/g, ' ');

/** Namespaces action codes inside the status dropdown so an action can never be mistaken for one
 *  of the five statuses — they are different kinds of thing sharing one control. */
const ACTION_PREFIX = 'action:';


/** What this invoice's status actually is: its own ruling when it has one, otherwise its
 *  vendor's. A row under a signed-off vendor is signed off — checking only `row_status` left
 *  those rows showing an unticked box beside a group header reading "Signed off". */
const effectiveStatus = (r: { rowStatus: string | null; finding?: GstFinding | null }) =>
	r.rowStatus ?? r.finding?.status ?? null;
const isSignedOff = (r: { rowStatus: string | null; finding?: GstFinding | null }) =>
	effectiveStatus(r) === 'reviewed_ok';

/** What this row itself amounts to, regardless of what its vendor's headline says.
 *
 *  This distinction is the whole point: AMRITVANI's four rows all carry the vendor sentence
 *  "...filed Rs 1,67,807 and then issued credit notes", which reads as nothing is missing —
 *  yet one of the four is ₹15,374 still owed. The row's own basis has to be visible on the row. */
const BASIS_TONE: Record<string, Tone> = {
	nullified: 'emerald',
	liable: 'rose',
	not_investigated: 'slate',
	matched: 'emerald',
};
const BASIS_LABEL: Record<string, string> = {
	nullified: 'Nothing owed',
	liable: 'Still owed',
	not_investigated: 'Not looked at',
	matched: 'Matched',
};

/**
 * Everything searchable goes in the label, because <Dropdown> filters on the label text and
 * nothing else — a field left out of it is a field nobody can search by.
 *
 * All spellings, so a vendor is findable by whichever one the reader has in mind: the same
 * vendor is "M.S.TRADING" in one system and "MS TRADING" in the other, for 211 of the 787.
 * The GSTIN too, because pasting one straight in is how you look up a vendor you already have
 * a number for. Names lead so the list stays readable when scrolled rather than searched.
 */
const vendorLabel = (v: GstVendor, statusCount: number) =>
	[v.names.join(' · '), v.gstin].filter(Boolean).join(' · ') +
	` — ${statusCount.toLocaleString()} here` +
	/* Labelled "unreconciled", not left bare, because value_unreconciled spans every problem
	   bucket this vendor sits in — it is NOT this tab's money. Printing it next to a per-tab
	   count without saying so is how "18 rows · ₹46.42L" ended up on a tab holding 0 of each. */
	(v.value_unreconciled ? ` · ${inr(v.value_unreconciled)} unreconciled` : '');

/** A row normalised to one shared shape so purchases and sales can render through the
 *  same table — government-side and Zoho-side fields differ in name (supplier vs
 *  recipient, bill vs invoice) but not in what they mean on screen. */
interface NormalizedRow {
	key: string;
	id: number;
	status: string;
	govName: string | null;
	govGstin: string | null;
	govInvoiceNumber: string | null;
	govDate: string | null;
	govValue: number | null;
	zohoName: string | null;
	zohoInvoiceNumber: string | null;
	zohoDate: string | null;
	zohoTotal: number | null;
	zohoStatus: string | null;
	diff: number | null;
	reason: string;
	tags: GstMismatchTag[];
	/** purchase-side only: Zoho's own bill_number field listed more than one invoice, so the
	 *  matcher summed the matching 2B lines — govInvoiceNumber/govValue above are that sum. */
	isBundle: boolean;
	/** purchase-side only: the investigated cause for this row's supplier. null/undefined means
	 *  nobody has looked at that supplier yet. Sales rows never carry one. */
	finding?: GstFinding | null;
	/** Purchase-side split of this row's own value. null on sales and on MATCHED rows, where the
	 *  API sends null rather than 0 — so these must never be coerced to zero. */
	rowStatus: string | null;
	rowAction: GstPurchaseRow['row_action'];
	nullifiedRs: number | null;
	liableRs: number | null;
	basis: string | null;
	/** true only when we hold a GSTIN for this vendor AND it disagrees with the filed one. */
	gstinDiffers: boolean;
	ourGstin: string | null;
}

const normalizePurchaseRow = (r: GstPurchaseRow, i: number): NormalizedRow => ({
	key: `${r.status}-${i}-${r.gstr2b?.invoice_number || r.zoho_bill?.bill_number || i}`,
	id: r.id,
	status: r.status,
	govName: r.gstr2b?.supplier_name ?? null,
	govGstin: r.gstr2b?.supplier_gstin ?? null,
	govInvoiceNumber: r.gstr2b?.invoice_number ?? null,
	govDate: r.gstr2b?.invoice_date ?? null,
	govValue: r.gstr2b?.invoice_value ?? null,
	zohoName: r.zoho_bill?.vendor_name ?? null,
	zohoInvoiceNumber: r.zoho_bill?.bill_number ?? null,
	zohoDate: r.zoho_bill?.date ?? null,
	zohoTotal: r.zoho_bill?.total ?? null,
	zohoStatus: null,
	diff: r.diff ?? null,
	reason: r.reason,
	tags: r.tags ?? [],
	isBundle: r.is_bundle ?? false,
	finding: r.finding ?? null,
	rowStatus: r.row_status ?? null,
	rowAction: r.row_action ?? null,
	nullifiedRs: r.nullified_rs ?? null,
	liableRs: r.liable_rs ?? null,
	basis: r.basis ?? null,
	gstinDiffers: r.zoho_bill?.gstin_matches === false,
	ourGstin: r.zoho_bill?.supplier_gstin ?? null,
});

const normalizeSalesRow = (r: GstSalesRow, i: number): NormalizedRow => ({
	key: `${r.status}-${i}-${r.gstr1?.invoice_number || r.zoho_invoice?.invoice_number || i}`,
	id: r.id,
	status: r.status,
	govName: r.gstr1?.recipient_name ?? null,
	govGstin: r.gstr1?.recipient_gstin ?? null,
	govInvoiceNumber: r.gstr1?.invoice_number ?? null,
	govDate: r.gstr1?.invoice_date ?? null,
	govValue: r.gstr1?.invoice_value ?? null,
	zohoName: r.zoho_invoice?.customer_name ?? null,
	zohoInvoiceNumber: r.zoho_invoice?.invoice_number ?? null,
	zohoDate: r.zoho_invoice?.invoice_date ?? null,
	zohoTotal: r.zoho_invoice?.total ?? null,
	zohoStatus: r.zoho_invoice?.status ?? null,
	diff: r.diff ?? null,
	reason: r.reason,
	tags: r.tags ?? [],
	isBundle: false,
	rowStatus: null,
	rowAction: null,
	nullifiedRs: null,
	liableRs: null,
	basis: null,
	gstinDiffers: false,
	ourGstin: null,
});

/** One side (Purchases or Sales) — tab bar driven entirely by `/summary`, never hardcoded,
 *  so a future 6th bucket needs no frontend change. */
const GstSide: React.FC<{
	side: 'purchases' | 'sales';
	tabs: GstTabInfo[];
	/** '' = the whole snapshot window. Owned by the parent so the tab counts above the table
	 *  and the rows in it are always fetched for the same month. */
	period: string;
	/** GstVendor.value ('' = all vendors). Owned by the parent for the same reason `period` is:
	 *  /summary must be filtered by it too, or the tab counts describe a different set than
	 *  the rows below them. */
	vendor: string;
	/** the selected vendor's GSTIN, or null when none is selected or it has no GSTIN. Drives the
	 *  extra vendor tabs; the drill-down route validates 15 characters, so name-only vendors
	 *  (199 of 787) never reach it. */
	vendorGstin: string | null;
	/** Actions the API says exist. Empty on an older API, which then offers none. */
	rowActions: GstRowAction[];
	/** GstSide owns which bucket is selected, but the vendor picker lives in the parent and its
	 *  counts are per-bucket, so the parent is told. */
	onStatusChange: (status: string) => void;
	/** Tell the parent a write happened, so /summary is refetched — the tab counts and the cause
	 *  breakdown both move when a finding is signed off. */
	onDataChanged: () => void;
	fetchRows: (params: {
		status?: string;
		search?: string;
		period?: string;
		vendor?: string;
		/** purchase side only — the sales endpoint ignores it. */
		category?: string;
		finding_status?: string;
		page?: number;
		pageSize?: number | 'all';
	}) => Promise<{
		data: (GstPurchaseRow | GstSalesRow)[];
		pagination: GstPagination;
		totals?: GstTotals;
		by_category?: GstCategoryCount[];
	}>;
	exportRows: (params: {
		status?: string;
		search?: string;
		period?: string;
		vendor?: string;
		category?: string;
	}) => Promise<void>;
	normalize: (r: GstPurchaseRow | GstSalesRow, i: number) => NormalizedRow;
	govLabel: string;
	zohoLabel: string;
}> = ({ side, tabs, period, vendor, vendorGstin, rowActions, onStatusChange, onDataChanged, fetchRows, exportRows, normalize, govLabel, zohoLabel }) => {
	const [status, setStatus] = useState(tabs[0]?.status ?? '');
	const [searchInput, setSearchInput] = useState('');
	const [search, setSearch] = useState('');
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState<number | 'all'>(50);
	const [rows, setRows] = useState<NormalizedRow[] | null>(null);
	const [pagination, setPagination] = useState<GstPagination | null>(null);
	const [totals, setTotals] = useState<GstTotals | null>(null);
	/** The cause filter. '' = all causes. Only ever set while the IN_2B_NOT_IN_ZOHO bucket is
	 *  active — every other bucket clears it, because a cause that belongs to one bucket would
	 *  silently return zero rows in another. */
	const [category, setCategory] = useState('');
	const [byCategory, setByCategory] = useState<GstCategoryCount[] | null>(null);
	const [causeOpen, setCauseOpen] = useState(true);
	const [err, setErr] = useState<string | null>(null);
	const [exporting, setExporting] = useState(false);
	const [selectedId, setSelectedId] = useState<number | null>(null);
	/** null = the API's own order (by id, i.e. snapshot order). Sorting is client-side because
	 *  the API takes no sort parameter, so it only ever reorders the rows already loaded. */
	const [sortParty, setSortParty] = useState<'asc' | 'desc' | null>(null);
	const [exportingCauses, setExportingCauses] = useState(false);
	/** Group consecutive rows of the same party under a subtotal header. */
	const [grouped, setGrouped] = useState(false);
	/** finding id currently being written, so only that header's control disables. */
	const [savingId, setSavingId] = useState<number | null>(null);
	const [reloadKey, setReloadKey] = useState(0);
	/** The WHOLE bucket's position — deliberately not filtered by cause or search, because the
	 *  question "how much of this bucket is done" must not change answer when someone clicks a
	 *  cause to read it. Period and vendor are included: those change what the bucket tab itself
	 *  counts, so leaving them out would compare against a different total. */
	const [bucketStance, setBucketStance] = useState<GstTotals | null>(null);
	/** The signed-off slice of the same bucket. A second small call because no read returns it
	 *  alongside the rest, and it is the only figure that moves when somebody signs something. */
	const [signedStance, setSignedStance] = useState<GstTotals | null>(null);
	/** The same two figures scoped to the selected cause. The bucket bar deliberately ignores the
	 *  cause filter — clicking a cause to read it must not change the headline — so progress
	 *  WITHIN the cause someone is actually working needs its own line. */
	const [causeStance, setCauseStance] = useState<{ filed: number; signed: number } | null>(null);
	/** finding ids ticked for a bulk sign-off. Keyed on the finding, which IS the vendor —
	 *  verified one-to-one, so there is deliberately no way to sign off a single invoice. */
	/** row ids — a per-invoice ruling, sent as `rows`. Independent of the vendor selection above;
	 *  both can go in the same call. */
	const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
	const [writeMsg, setWriteMsg] = useState<{ tone: 'ok' | 'warn'; text: string } | null>(null);
	/** '' = showing the GST buckets. Otherwise the id of a vendor tab (bank/zoho/ledger/trace),
	 *  which replaces the table below with that tab's rows. */
	const [vendorTab, setVendorTab] = useState('');
	const [vendorHeader, setVendorHeader] = useState<GstVendorHeader | null>(null);

	/* The three headline figures and the extra tab list both come from /vendors/:gstin/header.
	   A failure here costs only the vendor tabs — the GST buckets below still work. */
	useEffect(() => {
		let on = true;
		setVendorHeader(null);
		setVendorTab('');
		if (!vendorGstin) return;
		getGstVendorHeader(vendorGstin, { period: period || undefined })
			.then(h => on && setVendorHeader(h))
			.catch(() => on && setVendorHeader(null));
		return () => {
			on = false;
		};
	}, [vendorGstin, period]);

	const activeTab = tabs.find(t => t.status === status);

	// Reset to this side's first tab whenever the side itself changes.
	useEffect(() => {
		setStatus(tabs[0]?.status ?? '');
		setSearchInput('');
		setSearch('');
		setPage(1);
		setPageSize(50);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [side]);

	useEffect(() => {
		onStatusChange(status);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status]);

	// Debounce the search box before it hits the API.
	useEffect(() => {
		const t = setTimeout(() => setSearch(searchInput.trim()), 300);
		return () => clearTimeout(t);
	}, [searchInput]);

	// Any change to what is being filtered — or to how many rows fit on a page — invalidates
	// the current page number: page 12 of a 29-page list does not exist once you ask for 250.
	useEffect(() => {
		setPage(1);
	}, [status, search, period, vendor, pageSize, category]);

	// A cause belongs to one bucket. Carrying it across would show an empty table and read as
	// "nothing here" rather than "that filter does not apply to this bucket".
	useEffect(() => {
		setCategory('');
		setByCategory(null);
		setCauseOpen(true);
	}, [status]);

	useEffect(() => {
		let on = true;
		setRows(null);
		fetchRows({
			status: status || undefined,
			search: search || undefined,
			period: period || undefined,
			vendor: vendor || undefined,
			category: category || undefined,
			page,
			pageSize,
		})
			.then(res => {
				if (!on) return;
				setRows(res.data.map((r, i) => normalize(r, i)));
				setPagination(res.pagination);
				setTotals(res.totals ?? null);
				// Keep the last breakdown while a cause is selected — recomputing it from the
				// filtered set would collapse the panel to the single row just picked, and the
				// point of the panel is to keep the whole picture visible while drilling in.
				if (!category) setByCategory(res.by_category ?? null);
			})
			.catch(e => on && setErr(e?.message || 'Failed to load'));
		return () => {
			on = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status, search, page, pageSize, period, vendor, category, reloadKey]);

	/* One workbook, a sheet per cause, for the whole bucket — the CSV above exports the ONE view
	   on screen, which is the wrong shape for working through 19 causes. Purchases only: the
	   cause breakdown does not exist on the sales side. */
	const handleExportCauses = () => {
		setExportingCauses(true);
		exportGstCausesWorkbook({
			status: status || undefined,
			search: search || undefined,
			period: period || undefined,
			vendor: vendor || undefined,
		}).finally(() => setExportingCauses(false));
	};

	/* The one write on this screen. Refetches rows and tab counts afterwards rather than patching
	   local state from `gap_now`: the response tells us the bucket's new position, but the row
	   list and the cause breakdown move too, and a half-updated screen is worse than a reload.
	   `gap_now` is used for the message, so the reader sees the effect immediately either way. */
	/* Names the consequence in the numbers rather than in adjectives: approving a vendor whose
	   balance is not zero marks money as reviewed that nobody has explained. Sweeping all 91
	   partly-explained vendors would do that to ₹4.4Cr, so the count and the amount are in the
	   prompt itself, not buried in a help text. */
	const signOffPrompt = (ids: number[], rowIds: number[], label: string) => {
		const owedInSel = (visibleRows ?? [])
			.filter(r => rowIds.includes(r.id))
			.reduce((t, r) => t + (r.liableRs ?? 0), 0);
		const vendorOwed = [...partyGroups.values()]
			.filter(g => g.findingId !== null && ids.includes(g.findingId) && g.balance > 0)
			.reduce((t, g) => t + g.balance, 0);
		const what = ids.length
			? ids.length === 1
				? `${label} — the whole vendor`
				: `${ids.length} vendors`
			: `${rowIds.length} invoice${rowIds.length === 1 ? '' : 's'}`;
		return (
			`Sign off ${what}?\n\nThis records you as the approver.\n\n` +
			/* Two different warnings, because the consequences differ. A vendor-level ruling with
			   an open balance is REFUSED and writes nothing. Ticked invoices that are themselves
			   unexplained do get approved — so that money really would be stamped reviewed. */
			(vendorOwed > 0
				? `${inrFull(
						vendorOwed
				  )} of that vendor is still unexplained, so the server will REFUSE it and write nothing. ` +
				  `Tick its explained invoices instead.\n\n`
				: '') +
			(owedInSel > 0
				? `${inrFull(
						owedInSel
				  )} of the ticked invoices is still owed. Approving marks that money reviewed.\n\n`
				: '') +
			`It can be set back.`
		);
	};

	const setFindingStatus = async (
		sel: { ids?: number[]; rows?: number[] },
		/** null when an action alone is being applied. */
		next: GstFindingStatus | null,
		label: string,
		note?: string,
		actionCode?: string
	) => {
		const ids = sel.ids ?? [];
		const rowIds = sel.rows ?? [];
		if (!ids.length && !rowIds.length) return;
		if (next === 'reviewed_ok' && !window.confirm(signOffPrompt(ids, rowIds, label))) return;
		setSavingId(ids.length === 1 && !rowIds.length ? ids[0] : -1);
		setWriteMsg(null);
		try {
			const res = await updateGstFindings({ ids, rows: rowIds }, next, note, actionCode);
			/* Branch on what came back, not on the HTTP status: every path is a 200 now, and an
			   all-missing call is an empty `updated` rather than an error. */
			const nDone = res.updated.length + (res.updated_rows?.length ?? 0);
			const refused = res.refused ?? [];
			if (!nDone) {
				setWriteMsg({
					tone: 'warn',
					/* The server's own sentence, verbatim — it names the amount and lists the three
					   ways forward. Any paraphrase of mine would be shorter and less useful. */
					text: refused.length
						? refused.map(r => r.reason).join(' ')
						: `Nothing was updated — ${res.not_found.length} finding(s) no longer exist.`,
				});
			} else {
				const stillOwed = res.updated.reduce((t, u) => t + (u.liable_rs ?? 0), 0);
				const missed = res.not_found.length
					? ` ${res.not_found.length} id(s) no longer exist.`
					: '';
				/* Name the figure that actually moved. The bucket tab keeps showing ₹3.96Cr because
				   /purchases serves the matcher snapshot; the gap lives in a separate reporting
				   view. Someone watching the tab sees nothing happen and concludes the sign-off
				   failed — which is exactly what happened here. */
				const gap = res.gap_now
					? ` Excluded from the gap: ${res.gap_now.rows_excluded.toLocaleString()} rows, ${inrFull(
							res.gap_now.value_excluded
					  )}. The bucket tab total does not change — it counts the snapshot, not the gap.`
					: '';
				setWriteMsg({
					/* Approved but still owed is the Cornet case — the bill was found, the GSTIN is
					   still wrong, so the money stays in the gap. Reporting a clean success there
					   would be a lie. */
					tone: stillOwed > 0 ? 'warn' : 'ok',
					text:
						`${
							nDone === 1 && res.updated.length === 1
								? label
								: [
										res.updated.length && `${res.updated.length} vendor(s)`,
										res.updated_rows?.length && `${res.updated_rows.length} invoice(s)`,
								  ]
										.filter(Boolean)
										.join(' and ')
						} set to ${
							actionCode
								? rowActions.find(a => a.code === actionCode)?.label ?? actionCode
								: FINDING_STATUS_LABEL[next as string] ?? next
						}.` +
						(refused.length
							? ` ${refused.length} vendor(s) refused: ${refused
									.map(r => r.reason)
									.join(' ')}`
							: '') +
						(stillOwed > 0
							? ` ${inrFull(stillOwed)} is still owed and stays in the gap.`
							: '') +
						/* The row list is the matcher snapshot; the gap is a separate reporting view.
						   Signed-off rows keep showing here, now reading Signed off. Saying so stops
						   the refetch looking broken. */
						' Rows stay listed, now reading Signed off.' +
						gap +
						missed,
				});
			}
			// Rows, cause breakdown and tab counts all move on a write; bump both so nothing is left
			// describing the state before it.
			setReloadKey(k => k + 1);
			onDataChanged();
		} catch (e) {
			/* Prefer the server's own sentence over axios's. An id the server rejects outright comes
			   back 404 with "No gst_vendor finding with id 99999999" in the body, while e.message is
			   only "Request failed with status code 404" — which tells the reader nothing.
			   (A bulk call where SOME ids exist returns 200 with the misses in `not_found`; it is
			   only the all-missing case that throws.) */
			const body = (e as { response?: { data?: { message?: string } } })?.response?.data;
			setWriteMsg({
				tone: 'warn',
				text: body?.message || (e as Error)?.message || 'Could not save that.',
			});
		} finally {
			setSavingId(null);
			setSelectedRows(new Set());
		}
	};

	useEffect(() => {
		let on = true;
		setBucketStance(null);
		fetchRows({
			status: status || undefined,
			period: period || undefined,
			vendor: vendor || undefined,
			page: 1,
			pageSize: 1,
		})
			.then(r => on && setBucketStance(r.totals ?? null))
			.catch(() => on && setBucketStance(null));
		fetchRows({
			status: status || undefined,
			period: period || undefined,
			vendor: vendor || undefined,
			finding_status: 'reviewed_ok',
			page: 1,
			pageSize: 1,
		})
			.then(r => on && setSignedStance(r.totals ?? null))
			.catch(() => on && setSignedStance(null));
		return () => {
			on = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status, period, vendor, reloadKey]);

	useEffect(() => {
		let on = true;
		setCauseStance(null);
		if (!category) return;
		const base = {
			status: status || undefined,
			period: period || undefined,
			vendor: vendor || undefined,
			category,
			page: 1,
			pageSize: 1 as const,
		};
		Promise.all([fetchRows(base), fetchRows({ ...base, finding_status: 'reviewed_ok' })])
			.then(([all, signed]) => {
				if (!on || all.totals?.filed_rs === undefined) return;
				setCauseStance({ filed: all.totals.filed_rs, signed: signed.totals?.filed_rs ?? 0 });
			})
			.catch(() => on && setCauseStance(null));
		return () => {
			on = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [category, status, period, vendor, reloadKey]);

	const handleExport = () => {
		setExporting(true);
		exportRows({
			status: status || undefined,
			search: search || undefined,
			period: period || undefined,
			vendor: vendor || undefined,
			category: category || undefined,
		}).finally(() => setExporting(false));
	};

	/* Group a party's rows together so they can be judged in one pass instead of hunting them
	   across the list. localeCompare, not <, so "Ånand" sorts next to "Anand" rather than after Z.
	   Falls back to the Zoho-side name because government-side rows carry one and Zoho-only rows
	   carry the other — a row is never nameless on both. */
	const visibleRows = useMemo(() => {
		// Grouping is meaningless on unsorted rows — a party's rows must be adjacent for a header
		// to sit above them, so turning it on sorts even if the user never clicked the column.
		if (!rows || (!sortParty && !grouped)) return rows;
		const dir = sortParty === 'desc' ? -1 : 1;
		return [...rows].sort(
			(a, b) =>
				dir * (a.govName || a.zohoName || '').localeCompare(b.govName || b.zohoName || '')
		);
	}, [rows, sortParty, grouped]);

	/* Only where the API sends the split. Sales rows never carry it, and on MATCHED it is null
	   rather than 0 — an empty column is better than a column of dashes. */
	const showLiable = !!visibleRows?.some(r => r.basis && r.basis !== 'matched');
	/* Feature-detect the write API. `filed_rs` arrives with the same backend release as
	   PATCH /gst-recon/findings, basis/liable_rs and row_status — an older API has none of them.
	   Without this the sign-off checkboxes still render (they only need `finding`, which is
	   older) and every click 404s. Frontend and backend deploy separately, so this has to be
	   read off the response rather than assumed. */
	const writeSupported = bucketStance?.filed_rs !== undefined;

	const colCount = 8 + (status === 'IN_2B_NOT_IN_ZOHO' ? 1 : 0) + (showLiable ? 1 : 0);

	/* Per-party subtotals, summed from the ROWS.
	   Deliberately not finding.reported_rs / explained_rs / liable_rs: those are vendor-level and
	   repeat identically on every row of that vendor, so they read as per-row figures and sum to
	   nonsense (AMRITVANI's 4 rows would total ₹6,71,228 against a real ₹1,67,807). They also
	   drift from their own rows on 2 of 98 vendors — by ₹1.4L on DARKS SECURITY — and where they
	   disagree the rows are the correct side. Summing rows is right in both cases.
	   `blockLiable` is kept only to flag that disagreement, never to display as the answer. */
	const partyKey = (r: NormalizedRow) => `${r.govGstin ?? ''}|${r.govName ?? r.zohoName ?? ''}`;
	const partyGroups = useMemo(() => {
		const m = new Map<
			string,
			{
				name: string;
				gstin: string | null;
				rows: number;
				rowIds: number[];
				cleanRowIds: number[];
				owedRows: number;
				filed: number;
				reversed: number;
				balance: number;
				findingId: number | null;
				blockLiable: number | null;
				status: string | null;
				evidence: number;
			}
		>();
		for (const r of visibleRows ?? []) {
			const k = partyKey(r);
			const g = m.get(k) ?? {
				name: r.govName ?? r.zohoName ?? '—',
				gstin: r.govGstin,
				rows: 0,
				rowIds: [] as number[],
				cleanRowIds: [] as number[],
				owedRows: 0,
				filed: 0,
				reversed: 0,
				balance: 0,
				findingId: r.finding?.id ?? null,
				blockLiable: r.finding?.liable_rs ?? null,
				/* Vendor-level, exactly like the money figures: all 75 multi-row findings share one
				   finding.id and one status. Repeating it per row read as "this row needs review",
				   which it never meant. */
				status: r.finding?.status ?? null,
				evidence: r.finding?.evidence_lines ?? 0,
			};
			g.rows += 1;
			/* Only rows still open to a ruling. One already signed off has nothing to send, and
			   counting it would leave the group checkbox looking unfinished forever. */
			if (r.finding && !isSignedOff(r)) {
				g.rowIds.push(r.id);
				if (r.basis === 'nullified') g.cleanRowIds.push(r.id);
			}
			if ((r.liableRs ?? 0) > 0) g.owedRows += 1;
			g.filed += r.govValue ?? r.zohoTotal ?? 0;
			g.reversed += r.nullifiedRs ?? 0;
			g.balance += r.liableRs ?? 0;
			m.set(k, g);
		}
		return m;
	}, [visibleRows]);

	/* The safe queue: balance zero AND nobody has signed it yet. Everything else leaves money
	   owed, so it gets a per-vendor read rather than a sweep. */
	/* The bucket's position, straight from the API's own totals: computed on the whole filtered
	   set before pagination, so this is the same number at 50/page and at All rows. An earlier
	   version summed the loaded rows and needed a "this is only page 1" caveat; that guard is
	   gone because the number no longer depends on what happens to be on screen. */
	/* Three figures, three different questions:
	     signed    — a human approved it. The ONLY one that moves when Asha works.
	     explained — the investigation accounted for the money (reversed_rs). Fixed by the data.
	     filed     — the bucket.
	   An earlier version made `explained` the headline, so signing 13 invoices moved nothing on
	   screen and the work looked ignored. */
	const stance = useMemo(() => {
		const t = bucketStance;
		if (!t || t.filed_rs === undefined) return null;
		const explained = t.reversed_rs ?? 0;
		const signed = signedStance?.filed_rs ?? 0;
		return {
			filed: t.filed_rs,
			explained,
			signed,
			readyToSign: Math.max(explained - signed, 0),
			unexplained: t.filed_rs - explained,
		};
	}, [bucketStance, signedStance]);

	/* The safe queue at ROW level: an invoice whose money is fully explained and which nobody has
	   ruled on yet. Row-level rather than vendor-level because a vendor with one unexplained
	   invoice still has explained ones worth approving — that vendor is refused as a whole but
	   its clean rows are not. */
	const cleanRowIds = useMemo(
		() =>
			(visibleRows ?? [])
				.filter(r => r.basis === 'nullified' && r.finding && !isSignedOff(r))
				.map(r => r.id),
		[visibleRows]
	);
	/* Money inside the current selection that is NOT explained — the number that should stop
	   someone mid-sweep. */
	const selectedOwed = useMemo(
		() => (visibleRows ?? []).filter(r => selectedRows.has(r.id)).reduce((t, r) => t + (r.liableRs ?? 0), 0),
		[visibleRows, selectedRows]
	);


	if (err) return <div className='p-6 text-red-600'>Error: {err}</div>;

	return (
		<div>
			{vendorHeader && <VendorFigures header={vendorHeader} />}

			<div className='flex flex-wrap gap-1.5 mb-3'>
				{tabs.map(t => (
					<button
						key={t.status}
						onClick={() => {
							setStatus(t.status);
							setVendorTab('');
						}}
						title={t.reason}
						className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition ${
							status === t.status && !vendorTab
								? 'border-blue-600 bg-blue-50 text-blue-700'
								: 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
						}`}>
						<span
							className={`h-1.5 w-1.5 rounded-full ${
								STATUS_TONE[t.status] === 'emerald'
									? 'bg-emerald-500'
									: STATUS_TONE[t.status] === 'amber'
									? 'bg-amber-500'
									: STATUS_TONE[t.status] === 'rose'
									? 'bg-rose-500'
									: 'bg-slate-300'
							}`}
						/>
						{tabLabel(t)} ({t.count})
						{t.value !== null && <span className='text-gray-400'>· {inr(t.value)}</span>}
					</button>
				))}

				{/* The vendor's own evidence, in the same tab row as the GST buckets — these answer
				    "did we actually pay this vendor?" where the buckets answer "does the return
				    agree with our books?". Separated by a rule so it is clear they are a different
				    question, not a sixth bucket. */}
				{vendorHeader && vendorHeader.tabs.length > 0 && (
					<>
						<span className='mx-1 self-center text-gray-300'>|</span>
						{vendorHeader.tabs.map(t => (
							<button
								key={t.tab}
								onClick={() => setVendorTab(t.tab)}
								title={t.note ?? undefined}
								className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
									vendorTab === t.tab
										? 'border-violet-600 bg-violet-50 text-violet-700'
										: 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
								}`}>
								{t.label} ({t.count.toLocaleString()})
							</button>
						))}
					</>
				)}
			</div>

			{activeTab && !vendorTab && (
				<p className='text-xs text-gray-500 mb-3'>{activeTab.reason}</p>
			)}

			{/* The cause breakdown — only for the one bucket that has findings behind it. */}
			{!vendorTab && status === 'IN_2B_NOT_IN_ZOHO' && byCategory && byCategory.length > 0 && (
				<CauseBreakdown
					rows={byCategory}
					value={category}
					onPick={setCategory}
					open={causeOpen}
					onToggle={() => setCauseOpen(o => !o)}
				/>
			)}

			{/* A vendor tab replaces everything below: the search box, page size and CSV export all
			    belong to the GST buckets and do not apply to bank or ledger rows. */}
			{vendorTab && vendorGstin ? (
				<VendorTabTable gstin={vendorGstin} tab={vendorTab} period={period} />
			) : (
				<>
			<div className='mb-3 flex flex-wrap items-center gap-2'>
				<input
					value={searchInput}
					onChange={e => setSearchInput(e.target.value)}
					placeholder='Search vendor/customer name, GSTIN, or invoice number…'
					className='w-full max-w-md rounded border border-gray-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
				/>
				{/* Always rendered, never gated on row count — "All rows" has to be reachable on
				    the big tabs, which are exactly the ones someone wants to export or scan whole. */}
				<PageSizeSelect value={pageSize} onChange={setPageSize} />
				<button
					onClick={handleExport}
					disabled={exporting}
					className='flex items-center gap-1.5 rounded border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50'>
					{exporting ? <Loader2 size={13} className='animate-spin' /> : <Download size={13} />}
					Export CSV
				</button>
				<button
					onClick={() => setGrouped(g => !g)}
					title='Put every row of a party together under one subtotal line'
					className={`rounded border px-2.5 py-1.5 text-xs font-medium ${
						grouped
							? 'border-blue-600 bg-blue-50 text-blue-700'
							: 'border-gray-200 text-gray-600 hover:bg-gray-50'
					}`}>
					Group by party
				</button>
				{/* Only where causes exist. `by_category` is a purchase-side idea; offering this on
				    sales would download a workbook with one sheet called "Not yet investigated". */}
				{!!byCategory?.length && (
					<button
						onClick={handleExportCauses}
						disabled={exportingCauses}
						title='One .xlsx: a summary sheet plus one sheet per cause, covering the whole bucket — not just the rows on screen.'
						className='flex items-center gap-1.5 rounded border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50'>
						{exportingCauses ? (
							<Loader2 size={13} className='animate-spin' />
						) : (
							<Download size={13} />
						)}
						Export all causes (.xlsx)
					</button>
				)}
			</div>

			{/* The API takes no sort parameter, so this reorders only what is on screen. With more
			    than one page that defeats the point of grouping a party together — say so, and
			    offer the fix rather than leaving a half-sorted list looking whole. */}
			{sortParty && pagination && !pagination.all && pagination.totalPages > 1 && (
				<p className='mb-2 rounded border border-amber-200 bg-amber-50/50 p-2 text-[11px] text-amber-800'>
					Sorting page {pagination.page} of {pagination.totalPages} only — the other rows for a
					party may be on another page.{' '}
					<button onClick={() => setPageSize('all')} className='font-medium underline'>
						Show all {pagination.total.toLocaleString()} rows
					</button>{' '}
					to group them properly.
				</p>
			)}

			{/* Sits under the bucket tabs. The headline is what has been SIGNED OFF, because that
			    is what moves when somebody works — an earlier version showed "explained", which is
			    set by the investigation and never budged no matter how much Asha approved. */}
			{stance && stance.filed > 0 && (
				<div className='mb-3 rounded-lg border border-gray-200 bg-gray-50/60 px-3 py-2.5'>
					<div className='mb-1.5 flex flex-wrap items-baseline gap-x-2'>
						<span className='text-sm font-semibold text-gray-900'>
							{inrFull(stance.signed)} of {inrFull(stance.filed)} signed off
						</span>
						<span className='text-xs font-medium text-gray-500'>
							({Math.round((stance.signed / stance.filed) * 100)}%)
						</span>
						<span className='ml-auto text-xs'>
							<span className='font-semibold text-rose-700'>
								{inrFull(stance.unexplained)}
							</span>
							<span className='text-gray-500'> still unexplained</span>
						</span>
					</div>
					<div className='mb-1.5 flex h-2 overflow-hidden rounded bg-gray-200'>
						<div
							className='bg-emerald-600'
							style={{ width: `${(stance.signed / stance.filed) * 100}%` }}
							title={`Signed off: ${inrFull(stance.signed)}`}
						/>
						<div
							className='bg-emerald-300'
							style={{ width: `${(stance.readyToSign / stance.filed) * 100}%` }}
							title={`Explained, waiting for a sign-off: ${inrFull(stance.readyToSign)}`}
						/>
					</div>
					{/* The cause being worked right now, so "how much is left in THIS pile" is answerable
					    without doing arithmetic against the bucket. */}
					{causeStance && causeStance.filed > 0 && (
						<div className='mb-1.5 flex flex-wrap items-baseline gap-x-2 border-t border-gray-200 pt-1.5 text-xs'>
							<span className='font-medium text-gray-700'>{causeLabel(category)}</span>
							<span className='text-gray-900'>
								<span className='font-semibold'>{inrFull(causeStance.signed)}</span> of{' '}
								{inrFull(causeStance.filed)} signed off
							</span>
							<span className='text-gray-400'>
								({Math.round((causeStance.signed / causeStance.filed) * 100)}%)
							</span>
							<span className='ml-auto text-rose-700'>
								{inrFull(causeStance.filed - causeStance.signed)} left in this cause
							</span>
						</div>
					)}

					<div className='flex flex-wrap gap-x-4 text-[11px]'>
						<span className='text-emerald-700'>
							<span className='mr-1 inline-block h-2 w-2 rounded-sm bg-emerald-600 align-middle' />
							Signed off <span className='font-mono font-medium'>{inrFull(stance.signed)}</span>
						</span>
						<span className='text-emerald-600'>
							<span className='mr-1 inline-block h-2 w-2 rounded-sm bg-emerald-300 align-middle' />
							Explained, ready to sign{' '}
							<span className='font-mono font-medium'>{inrFull(stance.readyToSign)}</span>
						</span>
						<span className='text-gray-500'>
							<span className='mr-1 inline-block h-2 w-2 rounded-sm bg-gray-300 align-middle' />
							Nobody has explained yet{' '}
							<span className='font-mono font-medium'>{inrFull(stance.unexplained)}</span>
						</span>
					</div>
				</div>
			)}

			{/* Only meaningful when rows are grouped — a finding is a vendor, so a selection of
			    vendors is a selection of group headers. */}
			{grouped && writeSupported && cleanRowIds.length + selectedRows.size > 0 && (
				<div className='mb-2 flex flex-wrap items-center gap-2 rounded border border-gray-200 bg-gray-50/60 p-2 text-[11px]'>
					<span className='font-medium text-gray-700'>
						{selectedRows.size} invoice{selectedRows.size === 1 ? '' : 's'} selected
					</span>
					{/* The groups only exist for rows that are LOADED. On page 1 of 29 this button
					    offers the clean vendors on that page, not every clean vendor in the bucket —
					    which would read as "all done" after signing off a fraction. Say so, and put
					    the fix one click away. */}
					{pagination && !pagination.all && pagination.totalPages > 1 && (
						<span className='text-amber-700'>
							page {pagination.page} of {pagination.totalPages} only —{' '}
							<button onClick={() => setPageSize('all')} className='font-medium underline'>
								show all {pagination.total.toLocaleString()} rows
							</button>{' '}
							to select across the whole bucket
						</span>
					)}
					{cleanRowIds.length > 0 && (
						<button
							onClick={() => setSelectedRows(new Set(cleanRowIds))}
							title='Invoices whose money is fully explained and which nobody has ruled on yet — safe to approve without reading each one.'
							className='rounded border border-gray-200 bg-white px-2 py-1 font-medium text-gray-600 hover:bg-gray-50'>
							Select the {cleanRowIds.length} fully explained
						</button>
					)}
					{selectedRows.size > 0 && (
						<>
							<button
								onClick={() => setSelectedRows(new Set())}
								className='rounded border border-gray-200 bg-white px-2 py-1 text-gray-600 hover:bg-gray-50'>
								Clear
							</button>
							<button
								onClick={() =>
									setFindingStatus(
										{ rows: [...selectedRows] },
										'reviewed_ok',
										''
									)
								}
								disabled={savingId !== null}
								className='rounded border border-emerald-600 bg-emerald-50 px-2 py-1 font-medium text-emerald-700 disabled:opacity-50'>
								Sign off {selectedRows.size}
							</button>
							{/* The number that should stop someone mid-sweep. */}
							{selectedOwed > 0 && (
								<span className='text-amber-700'>
									{inrFull(selectedOwed)} of the selection is still owed — it will stay in
									the gap
								</span>
							)}
						</>
					)}
				</div>
			)}

			{writeMsg && (
				<div
					className={`mb-2 flex items-start justify-between gap-3 rounded border p-2 text-[11px] ${
						writeMsg.tone === 'ok'
							? 'border-emerald-200 bg-emerald-50/60 text-emerald-800'
							: 'border-amber-200 bg-amber-50/60 text-amber-800'
					}`}>
					<span>{writeMsg.text}</span>
					<button onClick={() => setWriteMsg(null)} className='shrink-0 font-medium underline'>
						dismiss
					</button>
				</div>
			)}

			<div className='overflow-x-auto rounded-lg border border-gray-200'>
				<table className='w-full text-sm'>
					<thead className='bg-gray-50 text-xs uppercase text-gray-500'>
						<tr>
							<th className='py-2 pl-3 pr-3 text-left'>Invoice #</th>
							<th className='py-2 pr-3 text-left'>
								<button
									onClick={() =>
										setSortParty(d => (d === null ? 'asc' : d === 'asc' ? 'desc' : null))
									}
									className='inline-flex items-center gap-1 uppercase hover:text-gray-700'
									title='Group this party&apos;s rows together'>
									Party
									<span className={sortParty ? 'text-blue-600' : 'text-gray-300'}>
										{sortParty === 'desc' ? '↓' : '↑'}
									</span>
								</button>
							</th>
							<th className='py-2 pr-3 text-left whitespace-nowrap'>Date</th>
							<th className='py-2 pr-3 text-right whitespace-nowrap'>{govLabel} Value</th>
							<th className='py-2 pr-3 text-right whitespace-nowrap'>{zohoLabel} Total</th>
							<th className='py-2 pr-3 text-right whitespace-nowrap'>Diff</th>
							{showLiable && (
								<th className='py-2 pr-3 text-right whitespace-nowrap' title='Still owed on this row. The rest of its value was cancelled by a credit note or already booked elsewhere.'>
									Still owed
								</th>
							)}
							<th className='py-2 pr-3 text-left'>Tags</th>
							<th className='py-2 pr-3 text-left'>Why</th>
							{status === 'IN_2B_NOT_IN_ZOHO' && (
								<th className='py-2 pr-3 text-left whitespace-nowrap'>Status</th>
							)}
						</tr>
					</thead>
					<tbody>
						{rows === null ? (
							<tr>
								<td colSpan={8 + (status === 'IN_2B_NOT_IN_ZOHO' ? 1 : 0) + (showLiable ? 1 : 0)} className='py-8 text-center text-gray-400'>
									<Loader2 className='inline animate-spin' size={16} /> Loading…
								</td>
							</tr>
						) : visibleRows!.length === 0 ? (
							<tr>
								<td colSpan={8 + (status === 'IN_2B_NOT_IN_ZOHO' ? 1 : 0) + (showLiable ? 1 : 0)} className='py-8 text-center text-gray-400'>
									{category ? 'No rows for this cause.' : 'No rows in this bucket.'}
								</td>
							</tr>
						) : (
							visibleRows!.map((r, i, arr) => {
								const gk = partyKey(r);
								const g = grouped && (i === 0 || partyKey(arr[i - 1]) !== gk)
									? partyGroups.get(gk)
									: null;
								const muted = r.status === 'VOID_OR_DRAFT_CORRECTLY_NOT_IN_GSTR1';
								return (
									<React.Fragment key={r.key}>
									{g && (
										<tr className='border-t-2 border-gray-200 bg-gray-50/80 text-xs'>
											<td colSpan={colCount} className='px-3 py-2'>
												<div className='flex flex-wrap items-center gap-x-4 gap-y-1'>
													{/* Ticks the vendor's invoices, not its finding. Sign-off then travels as
													    `rows`, so unticking the one invoice that is still owed leaves a
													    selection the server accepts — a vendor-level ruling with an open
													    balance is refused outright. The header dropdown still does the
													    whole-vendor decision for anyone who wants it. */}
													{!!g.rowIds.length && (
														<input
															type='checkbox'
															checked={g.rowIds.every(id => selectedRows.has(id))}
															ref={el => {
																// Half-ticked is the honest state once someone unticks the
																// invoice that is still owed.
																if (el)
																	el.indeterminate =
																		g.rowIds.some(id => selectedRows.has(id)) &&
																		!g.rowIds.every(id => selectedRows.has(id));
															}}
															onChange={e => {
																const on = e.target.checked;
																setSelectedRows(prev => {
																	const n = new Set(prev);
																	g.rowIds.forEach(id => (on ? n.add(id) : n.delete(id)));
																	return n;
																});
															}}
															onClick={e => e.stopPropagation()}
															className='h-3.5 w-3.5 cursor-pointer'
														/>
													)}
													<span className='font-semibold text-gray-900'>{g.name}</span>
													{g.gstin && (
														<span className='font-mono text-[11px] text-gray-400'>{g.gstin}</span>
													)}
													<span className='text-gray-500'>{g.rows} rows</span>
													<span className='text-gray-500'>
														Filed{' '}
														<span className='font-mono text-gray-800'>{inrFull(g.filed)}</span>
													</span>
													<span className='text-gray-500'>
														Reversed{' '}
														<span className='font-mono text-emerald-700'>{inrFull(g.reversed)}</span>
													</span>
													<span className='text-gray-500'>
														Balance{' '}
														<span
															className={`font-mono font-semibold ${
																g.balance > 0 ? 'text-rose-700' : 'text-gray-500'
															}`}>
															{inrFull(g.balance)}
														</span>
													</span>
													{/* The stored vendor figure disagreeing with its own rows is worth seeing,
													    not hiding — it happens on 2 of 98 vendors and the rows are the
													    correct side. */}
													{g.status && (
														<span
															className={`rounded border px-1.5 py-0.5 text-[11px] font-medium ${findingStatusTone(
																g.status
															)}`}
															title={
																g.balance === 0
																	? 'The money is explained. Signing off is a separate step — somebody still has to confirm the explanation holds.'
																	: undefined
															}>
															{FINDING_STATUS_LABEL[g.status] ?? g.status}
														</span>
													)}
													{!!g.evidence && (
														<span className='text-[11px] text-gray-400'>{g.evidence} evidence</span>
													)}
													{/* The status IS the control — five states, one dropdown, rather than an
													    Approve button plus a separate way back. Sits on the group because a
													    finding belongs to the vendor, not to one invoice. */}
													{g.findingId !== null && writeSupported && (
														<select
															/* Never let the sentinel become the selected value — it is an action,
															   not a state the finding can be in. */
															value={g.status ?? ''}
															disabled={savingId === g.findingId}
															onClick={e => e.stopPropagation()}
															onChange={e => {
																const v = e.target.value;
																const act = rowActions.find(a => `${ACTION_PREFIX}${a.code}` === v);
																if (act) {
																	/* Action alone — no status. The server derives the status from
																	   the action, so sending one would overstate what was decided
																	   here. The optional note is the reason, appended not replaced. */
																	const why = window.prompt(
																		`${act.label} — ${g.name}\n\n${
																			act.help ?? ''
																		}\n\nAdd a note for ${act.owner.replace(
																			/_/g,
																			' '
																		)} (optional):`
																	);
																	if (why === null) return; // cancelled; empty string is a valid "no note"
																	setFindingStatus(
																		{ ids: [g.findingId as number] },
																		null,
																		g.name,
																		why.trim() || undefined,
																		act.code
																	);
																	return;
																}
																setFindingStatus(
																	{ ids: [g.findingId as number] },
																	v as GstFindingStatus,
																	g.name
																);
															}}
															className='rounded border border-gray-200 bg-white px-1.5 py-0.5 text-[11px] text-gray-600 disabled:opacity-50'>
															{/* Offered by the API, never hardcoded — the dropdown and the server
															    cannot disagree about which actions exist. `help` sits on the
															    option so "do NOT ask the vendor to resend it" is read at the
															    moment of choosing. */}
															{rowActions.map(a => (
																<option
																	key={a.code}
																	value={`${ACTION_PREFIX}${a.code}`}
																	title={a.help ?? undefined}>
																	{a.label} →
																</option>
															))}
															{Object.entries(FINDING_STATUS_LABEL).map(([v, l]) => (
																<option
																	key={v}
																	value={v}
																	/* Refused server-side while any of this vendor's money is
																	   unexplained, so offering it only produces a failure. */
																	disabled={v === 'reviewed_ok' && g.balance > 0}>
																	{l}
																	{v === 'reviewed_ok' && g.balance > 0 ? ' — balance open' : ''}
																</option>
															))}
														</select>
													)}
													{/* A vendor with an open balance can never be signed off as a whole — the
													    server refuses it. Rather than leave the dropdown as the obvious thing
													    to click and let it fail, put the action that DOES work on the same
													    line: approve its explained invoices. */}
													{writeSupported && g.balance > 0 && g.cleanRowIds.length > 0 && (
														<button
															onClick={e => {
																e.stopPropagation();
																setFindingStatus(
																	{ rows: g.cleanRowIds },
																	'reviewed_ok',
																	g.name
																);
															}}
															disabled={savingId !== null}
															className='rounded border border-emerald-600 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 disabled:opacity-50'>
															Sign off {g.cleanRowIds.length} explained
														</button>
													)}
													{savingId === g.findingId && (
														<Loader2 size={12} className='animate-spin text-gray-400' />
													)}
													{g.blockLiable !== null && Math.abs(g.blockLiable - g.balance) > 1 && (
														<Tag
															tone='amber'
															title={`The stored vendor total says ${inrFull(
																g.blockLiable
															)}. These rows add up to ${inrFull(g.balance)} — the rows are right.`}>
															vendor total disagrees
														</Tag>
													)}
												</div>
											</td>
										</tr>
									)}
									<tr
										onClick={() => setSelectedId(r.id)}
										className={`cursor-pointer border-t border-gray-100 hover:bg-blue-50/40 ${
											muted ? 'text-gray-400' : 'text-gray-800'
										}`}>
										<td className='py-2 pl-3 pr-3 font-mono text-xs'>
											{r.govInvoiceNumber || r.zohoInvoiceNumber || '—'}
											{r.isBundle && (
												<Tag
													tone='slate'
													title="Zoho's bill_number field listed more than one invoice — these 2B lines were summed to match it">
													bundle
												</Tag>
											)}
										</td>
										<td className='py-2 pr-3'>
											<div>{r.govName || r.zohoName || '—'}</div>
											{r.govGstin && (
												<div className='font-mono text-[11px] text-gray-400'>{r.govGstin}</div>
											)}
										</td>
										<td className='py-2 pr-3 whitespace-nowrap'>
											{shortDate(r.govDate ?? r.zohoDate)}
										</td>
										<td className='py-2 pr-3 text-right font-mono whitespace-nowrap'>
											{r.govValue !== null ? inrFull(r.govValue) : '—'}
										</td>
										<td className='py-2 pr-3 text-right font-mono whitespace-nowrap'>
											{r.zohoTotal !== null ? inrFull(r.zohoTotal) : '—'}
											{r.zohoStatus && (
												<div className='text-[11px] font-sans text-gray-400'>{r.zohoStatus}</div>
											)}
										</td>
										<td
											className={`py-2 pr-3 text-right font-mono whitespace-nowrap ${
												r.diff ? 'font-medium text-rose-600' : 'text-gray-300'
											}`}>
											{r.diff ? inrFull(r.diff) : '—'}
										</td>
										<td className='py-2 pr-3'>
											<div className='flex flex-wrap gap-1'>
												{r.tags.map(t => (
													<Tag key={t} tone={TAG_TONE[t]}>
														{TAG_LABEL[t]}
													</Tag>
												))}
											</div>
										</td>
										{/* The bucket's own definition is already printed once above the table.
										    Repeating it on every row taught nobody anything; the row's own
										    investigated cause goes here instead, and r.reason is the fallback
										    for buckets that have no findings. */}
										<td className='py-2 pr-3 min-w-[200px] max-w-xs text-xs text-gray-500'>
											{r.finding ? (
												<>
													<div className='flex flex-wrap items-center gap-1.5'>
														<span className='font-medium text-gray-700'>
															{causeLabel(r.finding.category)}
														</span>
														{/* The row's own verdict, next to the vendor's cause. Without it the
														    headline below — a VENDOR sentence — speaks for a row it may be
														    wrong about. */}
														{r.basis && r.basis !== 'matched' && (
															<Tag tone={BASIS_TONE[r.basis] ?? 'slate'}>
																{BASIS_LABEL[r.basis] ?? r.basis}
															</Tag>
														)}
														{/* Toned by basis, never by the link's existence: six of the seven
														    GSTIN-mismatch rows are fully cancelled, one is ₹1.92L owed. */}
														{r.gstinDiffers && (
															<Tag
																tone={r.basis === 'liable' ? 'rose' : 'amber'}
																title={`Filed under ${r.govGstin ?? '—'}, we hold ${r.ourGstin ?? '—'}`}>
																GSTIN differs
															</Tag>
														)}
													</div>
													{/* Row-level fact wins over the vendor sentence when the investigation
													    recorded one. */}
													{(r.finding.row_summary || r.finding.headline) && (
														<div className='mt-0.5 line-clamp-2 text-[11px] text-gray-400'>
															{r.finding.row_summary || r.finding.headline}
														</div>
													)}
												</>
											) : status === 'IN_2B_NOT_IN_ZOHO' ? (
												<span className='text-gray-400'>Not yet investigated</span>
											) : (
												r.reason
											)}
										</td>
										{status === 'IN_2B_NOT_IN_ZOHO' && (
											<td className='py-2 pr-3 whitespace-nowrap'>
												{/* `row_status` is this invoice's OWN ruling and wins over its vendor's: after
												    signing off three of AMRITVANI's four, the fourth must still read Needs
												    review. Where no row ruling exists yet the row is tickable — the only way
												    to approve the explained invoices of a vendor whose balance is open,
												    since a vendor-level sign-off there is refused outright. */}
												{grouped && writeSupported && r.finding && !isSignedOff(r) ? (
													<label
														className='flex cursor-pointer items-center gap-1.5'
														onClick={e => e.stopPropagation()}>
														<input
															type='checkbox'
															checked={selectedRows.has(r.id)}
															onChange={e =>
																setSelectedRows(prev => {
																	const n = new Set(prev);
																	if (e.target.checked) n.add(r.id);
																	else n.delete(r.id);
																	return n;
																})
															}
															className='h-3.5 w-3.5'
														/>
														<span className='text-[11px] text-gray-400'>sign off</span>
													</label>
												) : r.rowAction || r.rowStatus || r.finding ? (
													<>
														{/* The ACTION wins the label when one is set. "In progress" only says
														    the row moved; "Record in Zoho" says what has to happen and who
														    does it, which is what somebody opening this screen needs. */}
														<span
															className={`rounded border px-1.5 py-0.5 text-[11px] font-medium ${
																r.rowAction
																	? 'border-violet-200 bg-violet-50 text-violet-700'
																	: findingStatusTone(r.rowStatus ?? r.finding?.status ?? '')
															}`}
															title={r.rowAction?.required ?? undefined}>
															{r.rowAction?.label ??
																FINDING_STATUS_LABEL[r.rowStatus ?? r.finding?.status ?? ''] ??
																r.rowStatus ??
																r.finding?.status}
														</span>
														{r.rowAction && (
															<div className='mt-0.5 text-[10px] text-gray-400'>
																{r.rowAction.owner.replace(/_/g, ' ')}
															</div>
														)}
													</>
												) : (
													<span className='text-[11px] text-gray-300'>—</span>
												)}
												{!!r.finding?.evidence_lines && (
													<div className='mt-0.5 text-[10px] text-gray-400'>
														{r.finding.evidence_lines} evidence
													</div>
												)}
											</td>
										)}
									</tr>
									</React.Fragment>
								);
							})
						)}
					</tbody>
				</table>
			</div>

			{pagination && pagination.total > 0 && (
				<div className='mt-2 flex items-center justify-between text-xs text-gray-500'>
					<span>
						{pagination.total.toLocaleString()} rows
						{/* Money for the whole filtered set, not just this page. Suppressed when
						    valued_rows is 0 — on MATCHED every row has two amounts rather than one,
						    so the sum is 0 and printing "₹0" would read as "no money here". */}
						{!!totals && totals.valued_rows > 0 && (
							<>
								{' · '}
								<span className='font-medium text-gray-700'>{inrFull(totals.value)}</span>
								{totals.valued_rows < totals.count &&
									` across ${totals.valued_rows.toLocaleString()} of them`}
							</>
						)}
						{/* Summed from the ROWS, because the stored vendor-level liable_rs drifts from
						    its own rows on 2 of 98 vendors — fine for a headline, not for a figure
						    anyone might carry into a return.
						    Shown only when every row is loaded: a per-page sum of "still owed" is a
						    smaller number that looks like the whole answer. */}
						{showLiable && pagination.all && (
							<>
								{' · '}
								<span className='font-medium text-rose-700'>
									{inrFull(
										(visibleRows ?? []).reduce((t, r) => t + (r.liableRs ?? 0), 0)
									)}
								</span>{' '}
								still owed
							</>
						)}
						{pagination.all ? ' · showing all' : ` · page ${pagination.page} of ${pagination.totalPages}`}
					</span>
					{!pagination.all && (
						<div className='flex gap-2'>
							<button
								disabled={page <= 1}
								onClick={() => setPage(p => Math.max(1, p - 1))}
								className='rounded border border-gray-200 px-2 py-1 disabled:opacity-40'>
								Prev
							</button>
							<button
								disabled={page >= pagination.totalPages}
								onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
								className='rounded border border-gray-200 px-2 py-1 disabled:opacity-40'>
								Next
							</button>
						</div>
					)}
				</div>
			)}
				</>
			)}

			{selectedId !== null && (
				<GstDetailDrawer
					side={side}
					id={selectedId}
					onClose={() => setSelectedId(null)}
					onNavigate={id => setSelectedId(id)}
				/>
			)}
		</div>
	);
};

/** Label/value pair used throughout the detail panel's breakdown tables. */
const Row: React.FC<{ label: string; children: React.ReactNode; strong?: boolean }> = ({
	label,
	children,
	strong,
}) => (
	<div className='flex items-baseline justify-between gap-3 py-1 text-sm'>
		<span className='text-gray-500'>{label}</span>
		<span className={`font-mono ${strong ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
			{children}
		</span>
	</div>
);

const GstDetailDrawer: React.FC<{
	side: 'purchases' | 'sales';
	id: number;
	onClose: () => void;
	onNavigate: (id: number) => void;
}> = ({ side, id, onClose, onNavigate }) => {
	const [detail, setDetail] = useState<GstPurchaseDetail | GstSalesDetail | null>(null);
	const [err, setErr] = useState<string | null>(null);
	const [attachment, setAttachment] = useState<GstAttachment | null>(null);
	const [attachmentLoading, setAttachmentLoading] = useState(false);
	const [attachmentErr, setAttachmentErr] = useState<string | null>(null);

	useEffect(() => {
		let on = true;
		setDetail(null);
		setAttachment(null);
		setAttachmentErr(null);
		setErr(null);
		const fetcher = side === 'purchases' ? getGstPurchaseDetail : getGstSalesDetail;
		fetcher(id)
			.then(d => on && setDetail(d))
			.catch(e => on && setErr(e?.message || 'Failed to load'));
		return () => {
			on = false;
		};
	}, [side, id]);

	const viewDocument = () => {
		setAttachmentLoading(true);
		setAttachmentErr(null);
		const fetcher = side === 'purchases' ? getGstPurchaseAttachment : getGstSalesAttachment;
		fetcher(id)
			.then(setAttachment)
			.catch(e => setAttachmentErr(e?.message || "Couldn't reach Zoho right now — try again."))
			.finally(() => setAttachmentLoading(false));
	};

	const isPurchase = side === 'purchases';
	const purchase = isPurchase ? (detail as GstPurchaseDetail | null) : null;
	const sales = !isPurchase ? (detail as GstSalesDetail | null) : null;

	const invoiceNumber =
		purchase?.gstr2b?.invoice_number ??
		purchase?.zoho_bill?.bill_number ??
		sales?.gstr1?.invoice_number ??
		sales?.zoho_invoice?.invoice_number ??
		'—';

	const hasAttachmentKnown = detail?.proof.zoho.has_attachment;
	const canViewDocument = isPurchase ? hasAttachmentKnown !== false : true;


	return (
		<div className='fixed inset-0 z-50 flex justify-end'>
			<div className='absolute inset-0 bg-black/30' onClick={onClose} />
			<div className='relative h-full w-full max-w-2xl overflow-y-auto bg-white shadow-2xl'>
				<div className='sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4'>
					<div>
						<h3 className='font-semibold text-gray-900'>{invoiceNumber}</h3>
						{detail && (
							<span
								className={`mt-1 inline-block rounded px-1.5 py-0.5 text-[11px] font-medium ${
									STATUS_TONE[detail.status] === 'emerald'
										? 'bg-emerald-50 text-emerald-700'
										: STATUS_TONE[detail.status] === 'amber'
										? 'bg-amber-50 text-amber-700'
										: STATUS_TONE[detail.status] === 'rose'
										? 'bg-rose-50 text-rose-700'
										: 'bg-slate-100 text-slate-600'
								}`}>
								{detail.status.replace(/_/g, ' ')}
							</span>
						)}
					</div>
					<button onClick={onClose} className='text-gray-400 hover:text-gray-600'>
						<X size={20} />
					</button>
				</div>

				{err && <div className='p-6 text-red-600'>Error: {err}</div>}

				{!detail && !err && (
					<div className='flex items-center gap-2 p-10 text-gray-400'>
						<Loader2 className='animate-spin' size={18} /> Loading…
					</div>
				)}

				{detail && (
					<div className='space-y-5 p-6'>
						{/* The bucket's definition — true of all 1,428 rows, so it is context, not an
						    answer. Demoted to small grey text now that the finding sits above it. */}
						{(detail as GstPurchaseDetail).finding ? (
							<FindingPanel finding={(detail as GstPurchaseDetail).finding as GstFindingDetail} />
						) : null}
						<p className='text-sm text-gray-700'>{detail.reason}</p>

						{detail.tags && detail.tags.length > 0 && (
							<div className='flex flex-wrap gap-1.5'>
								{detail.tags.map(t => (
									<Tag key={t} tone={TAG_TONE[t]}>
										{TAG_LABEL[t]}
									</Tag>
								))}
							</div>
						)}

						{/* Zoho's own bill_number field named more than one invoice — shown as its
						    real component lines, not just the merged gstr2b total below, per the
						    same "what were you actually looking at" answer the Awadh case needed. */}
						{purchase?.is_bundle && purchase.gstr2b_bundle && purchase.gstr2b_bundle.length > 0 && (
							<section className='rounded-lg border border-gray-200 p-4'>
								<h4 className='mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500'>
									Bundled invoices
								</h4>
								<p className='mb-2 text-xs text-gray-500'>
									Zoho recorded this as one bill covering more than one GSTR-2B invoice. The
									amount breakdown below compares their sum against the Zoho total.
								</p>
								<div className='divide-y divide-gray-100'>
									{purchase.gstr2b_bundle.map(line => (
										<div key={line.invoice_number} className='flex items-center justify-between py-1.5 text-sm'>
											<div>
												<div className='font-mono text-xs text-gray-800'>{line.invoice_number}</div>
												<div className='text-[11px] text-gray-400'>{shortDate(line.invoice_date)}</div>
											</div>
											<div className='font-mono text-gray-700'>{inrFull(line.invoice_value)}</div>
										</div>
									))}
									<div className='flex items-center justify-between py-1.5 text-sm font-semibold'>
										<span className='text-gray-600'>Sum</span>
										<span className='font-mono text-gray-900'>
											{inrFull(purchase.gstr2b_bundle_total ?? 0)}
										</span>
									</div>
								</div>
							</section>
						)}

						{/* Amount breakdown — purchase side walks the full GSTR-2B/TDS arithmetic;
						    sales side is a simple three-figure diff. Every number here is real and
						    sourced, per the brief — never estimated. */}
						{purchase?.amount_breakdown && (
							<section className='rounded-lg border border-gray-200 p-4'>
								<h4 className='mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500'>
									Amount breakdown
								</h4>
								<Row label='GSTR-2B invoice value'>
									{inrFull(purchase.amount_breakdown.gstr2b_invoice_value)}
								</Row>
								<Row label='GSTR-2B taxable value'>
									{inrFull(purchase.amount_breakdown.gstr2b_taxable_value)}
								</Row>
								<Row label='GSTR-2B tax (IGST/CGST/SGST/Cess)'>
									{inrFull(purchase.amount_breakdown.gstr2b_tax.igst)} /{' '}
									{inrFull(purchase.amount_breakdown.gstr2b_tax.cgst)} /{' '}
									{inrFull(purchase.amount_breakdown.gstr2b_tax.sgst)} /{' '}
									{inrFull(purchase.amount_breakdown.gstr2b_tax.cess)}
								</Row>
								<Row label='GSTR-2B computed gross' strong>
									{inrFull(purchase.amount_breakdown.gstr2b_computed_gross)}{' '}
									{purchase.amount_breakdown.gstr2b_gross_matches_stated_value ? (
										<Tag tone='emerald'>matches stated value</Tag>
									) : (
										<Tag tone='rose'>does not match stated value</Tag>
									)}
								</Row>
								<div className='my-1 border-t border-dashed border-gray-200' />
								<Row label='Zoho bill total'>{inrFull(purchase.amount_breakdown.zoho_bill_total)}</Row>
								<Row label='TDS withheld (from finrecon_bill_tds)'>
									{inrFull(purchase.amount_breakdown.tds_withheld)}
								</Row>
								<Row label='Zoho total + TDS'>
									{inrFull(purchase.amount_breakdown.zoho_total_plus_tds)}
								</Row>
								<Row label='Residual after TDS' strong>
									{inrFull(purchase.amount_breakdown.residual_after_tds)}{' '}
									{purchase.amount_breakdown.explained_by_tds ? (
										<Tag tone='emerald'>explained by TDS</Tag>
									) : (
										<Tag tone='rose'>not fully explained</Tag>
									)}
								</Row>
							</section>
						)}

						{sales?.amount_breakdown && (
							<section className='rounded-lg border border-gray-200 p-4'>
								<h4 className='mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500'>
									Amount breakdown
								</h4>
								<Row label='GSTR-1 invoice value'>
									{inrFull(sales.amount_breakdown.gstr1_invoice_value)}
								</Row>
								<Row label='Zoho invoice total'>{inrFull(sales.amount_breakdown.zoho_invoice_total)}</Row>
								<Row label='Diff' strong>
									{inrFull(sales.amount_breakdown.diff)}
								</Row>
							</section>
						)}

						{/* Sales-side insight — only present on IN_ZOHO_NOT_IN_GSTR1 rows, computed
						    per row from live checks, never a templated guess. */}
						{sales?.insight && (
							<section className='rounded-lg border border-amber-200 bg-amber-50/40 p-4'>
								<h4 className='mb-2 text-xs font-semibold uppercase tracking-wide text-amber-800'>
									Why this was likely missed
								</h4>
								<p className='mb-2 text-sm text-gray-800'>{sales.insight.finding}</p>
								<p className='mb-2 text-sm font-medium text-gray-900'>
									{sales.insight.likely_cause}
								</p>
								<p className='mb-3 text-sm text-gray-700'>
									<span className='font-medium'>Process fix: </span>
									{sales.insight.process_fix}
								</p>

								<div className='rounded border border-amber-300 bg-white p-3'>
									<p className='mb-2 text-sm font-semibold text-amber-900'>
										{sales.insight.remediation.note}
									</p>
									<ol className='list-decimal space-y-2 pl-4'>
										{sales.insight.remediation.options.map((o, i) => (
											<li key={i} className='text-sm text-gray-700'>
												<div className='font-medium text-gray-900'>{o.option}</div>
												<div className='text-xs text-gray-500'>When: {o.when}</div>
												<div className='text-xs text-gray-500'>Effect: {o.effect}</div>
											</li>
										))}
									</ol>
								</div>
							</section>
						)}

						{/* Proof — the two things a human checks this against. */}
						<section className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
							<div className='rounded-lg border border-gray-200 p-4'>
								<h4 className='mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500'>
									GST portal
								</h4>
								{detail.proof.gst_portal.file && (
									<div className='mb-1 break-all font-mono text-xs text-gray-700'>
										{detail.proof.gst_portal.file}
									</div>
								)}
								{(detail.proof.gst_portal.period || detail.proof.gst_portal.month) && (
									<div className='mb-1 text-xs text-gray-500'>
										{detail.proof.gst_portal.period || detail.proof.gst_portal.month}
									</div>
								)}
								<p className='text-xs text-gray-500'>{detail.proof.gst_portal.note}</p>
							</div>
							<div className='rounded-lg border border-gray-200 p-4'>
								<h4 className='mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500'>
									Zoho
								</h4>
								<a
									href={detail.proof.zoho.view_url}
									target='_blank'
									rel='noreferrer'
									className='mb-2 inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:underline'>
									Open in Zoho Books <ExternalLink size={11} />
								</a>
								<p className='mb-2 text-xs text-gray-500'>{detail.proof.zoho.note}</p>

								<button
									onClick={viewDocument}
									disabled={!canViewDocument || attachmentLoading}
									className='flex items-center gap-1.5 rounded border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40'>
									{attachmentLoading ? (
										<Loader2 size={13} className='animate-spin' />
									) : (
										<FileText size={13} />
									)}
									View document
								</button>
								{!canViewDocument && (
									<div className='mt-1 text-[11px] text-gray-400'>
										{detail.proof.zoho.attachment_note}
									</div>
								)}
								{attachmentErr && (
									<div className='mt-2 text-xs text-rose-600'>{attachmentErr}</div>
								)}
								{attachment?.kind === 'none' && (
									<div className='mt-2 text-xs text-gray-500'>{attachment.note}</div>
								)}
								{attachment?.kind === 'pdf' && (
									<div className='mt-3'>
										<a
											href={attachment.blobUrl}
											target='_blank'
											rel='noreferrer'
											className='mb-2 inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:underline'>
											Open in new tab <ExternalLink size={11} />
										</a>
										<iframe
											title='Document'
											src={attachment.blobUrl}
											className='h-96 w-full rounded border border-gray-200'
										/>
									</div>
								)}
							</div>
						</section>

						{detail.other_occurrences.length > 0 && (
							<section>
								<h4 className='mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500'>
									Also seen in
								</h4>
								<div className='flex flex-wrap gap-1.5'>
									{detail.other_occurrences.map(o => (
										<button
											key={o.id}
											onClick={() => onNavigate(o.id)}
											className='rounded border border-gray-200 px-2 py-1 text-xs text-blue-700 hover:bg-blue-50'>
											#{o.id} {o.period || o.month || shortDate(o.invoice_date)}
										</button>
									))}
								</div>
							</section>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

/**
 * GST reconciliation — do the government's numbers (GSTR-2B on purchases, GSTR-1 on
 * sales) agree with Zoho, invoice by invoice? Purchases and sales use different status
 * buckets (see `/summary`), so they get separate tab bars, not a shared one.
 */
export const GstReconciliation: React.FC = () => {
	const [summary, setSummary] = useState<GstSummary | null>(null);
	const [meta, setMeta] = useState<GstMeta | null>(null);
	const [err, setErr] = useState<string | null>(null);
	const [section, setSection] = useState<'purchases' | 'sales'>('purchases');
	/** '' = the whole snapshot window, which is the landing view. */
	const [period, setPeriod] = useState('');
	/** GstVendor.value — a GSTIN for the 588 vendors that have one, a name for the 199 that
	 *  don't. '' = all vendors. */
	const [vendor, setVendor] = useState('');
	const [vendors, setVendors] = useState<GstVendor[]>([]);
	/** which GST bucket the visible side is on — the vendor picker's counts are per-bucket. */
	const [activeStatus, setActiveStatus] = useState('');
	/** bumped after a write so the summary is refetched */
	const [dataVersion, setDataVersion] = useState(0);

	// Meta is the snapshot's description (source files, freshness, which months exist) — it
	// does not change with the selected month, so it is fetched once and never refetched.
	useEffect(() => {
		let on = true;
		getGstMeta()
			.then(m => on && setMeta(m))
			.catch(e => on && setErr(e?.message || 'Failed to load'));
		return () => {
			on = false;
		};
	}, []);

	// Summary carries the tab counts, so it refetches per month alongside the rows.
	// Deliberately NOT cleared to null first: blanking it would unmount <GstSide> and silently
	// throw away the user's tab choice and search text on every month change.
	useEffect(() => {
		let on = true;
		getGstSummary({ period: period || undefined, vendor: vendor || undefined })
			.then(s => on && setSummary(s))
			.catch(e => on && setErr(e?.message || 'Failed to load'));
		return () => {
			on = false;
		};
	}, [period, vendor, dataVersion]);

	/* The vendor list is per-side (suppliers vs customers) and narrows with the month, so it is
	   refetched when either changes. Clearing the selection on that change is deliberate: a
	   supplier GSTIN means nothing on the sales tab, and a vendor with no rows in the chosen
	   month would otherwise leave the screen filtered to an empty set with no visible cause.
	   A failed list only costs the dropdown, never the page — the rows below still render. */
	useEffect(() => {
		let on = true;
		setVendor('');
		setVendors([]);
		getGstVendors({ side: section, period: period || undefined })
			.then(v => on && setVendors(v))
			.catch(() => on && setVendors([]));
		return () => {
			on = false;
		};
	}, [section, period]);

	/* Only the 588 GSTIN-grouped vendors can drill down: the route validates 15 characters, so a
	   name-only vendor would 400. Read matched_by rather than testing the string's length —
	   "AV Water Supply" is coincidentally 15 characters and would sneak through as a GSTIN. */
	/* Scoped to the bucket on screen. `count` and `value_unreconciled` are all-bucket totals, so
	   using them here offered 407 of 785 vendors on IN_2B_NOT_IN_ZOHO that have zero rows in it —
	   picking one gave an empty table with no visible cause. by_status is the per-bucket truth.
	   Memoised because 785 option objects rebuilt on every keystroke of the dropdown's own search
	   box would be wasted work. */
	const vendorsInBucket = useMemo(
		() =>
			vendors
				.map(v => ({ v, n: v.by_status?.[activeStatus] ?? 0 }))
				.filter(x => x.n > 0),
		[vendors, activeStatus]
	);

	const vendorOptions = useMemo(
		() => [
			{
				value: '',
				label: `All ${section === 'purchases' ? 'vendors' : 'customers'} (${vendorsInBucket.length})`,
			},
			...vendorsInBucket.map(({ v, n }) => ({ value: v.value, label: vendorLabel(v, n) })),
		],
		[vendorsInBucket, section]
	);

	/* A vendor with no rows in the newly-selected bucket would leave the table filtered to an
	   empty set while the picker shows a blank box — same reasoning as clearing it on a month
	   change. Drop the selection instead. */
	useEffect(() => {
		if (vendor && vendors.length && !vendorsInBucket.some(x => x.v.value === vendor)) {
			setVendor('');
		}
	}, [vendorsInBucket, vendor, vendors.length]);

	const vendorGstin = useMemo(() => {
		const v = vendors.find(x => x.value === vendor);
		return v && v.matched_by === 'gstin' ? v.value : null;
	}, [vendors, vendor]);

	const generatedAt = useMemo(() => {
		if (!meta) return null;
		const d = new Date(meta.generated_at);
		return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
	}, [meta]);

	if (err) return <div className='p-6 text-red-600'>Error: {err}</div>;
	if (!summary || !meta)
		return (
			<div className='flex items-center gap-2 p-6 text-gray-500'>
				<Loader2 className='animate-spin' size={18} /> Loading GST reconciliation…
			</div>
		);

	return (
		<section className='bg-white rounded-lg border border-gray-200 p-5'>
			<div className='mb-4 flex flex-wrap items-center justify-between gap-2'>
				<div>
					<h3 className='font-semibold text-gray-900'>
						GST reconciliation — GSTR-2B/GSTR-1 vs Zoho
					</h3>
					<p className='text-xs text-gray-400'>
						{period
							? `${monthLabel(period)} return`
							: `${meta.window.from} → ${meta.window.to}`}{' '}
						· as of {generatedAt}
					</p>
				</div>
				<div className='flex items-center gap-2'>
					{/* `periods` only exists on an API that also understands ?period. Frontend and
					    backend deploy separately, so against an older API this is undefined —
					    render no picker at all rather than a dropdown whose only option is the
					    view you are already looking at. */}
					{!!meta.periods?.length && (
						/* Labelled "return month", not "month": this filters on the month the line
						   appeared in the government return, while the table's DATE column shows the
						   document date. For quarterly (QRMP) and late filers the two differ — pick
						   April and roughly half the rows read as March or January. Without the
						   label on screen that looks like a broken filter. */
						<label className='flex items-center gap-1.5 text-xs text-gray-500'>
							GST return month
							<select
								value={period}
								onChange={e => setPeriod(e.target.value)}
								title='The month the line appeared in the government return (GSTR-2B on purchases, GSTR-1 on sales) — the month its ITC is claimable. This is not the invoice date, and for quarterly or late filers the two fall in different months.'
								className='rounded border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500'>
								<option value=''>All months</option>
								{meta.periods.map(p => (
									<option key={p} value={p}>
										{monthLabel(p)}
									</option>
								))}
							</select>
						</label>
					)}
					{/* Same feature-detect as the month picker: no list, no control.
					    Uses the shared <Dropdown>, which is searchable by default — 785 options is
					    far past what a native select's first-letter jump can navigate, and its
					    filter matches anywhere in the label, so a vendor is findable by any of its
					    spellings or by pasting a GSTIN. */}
					{vendors.length > 0 && (
						<div
							className='flex items-center gap-1.5 text-xs text-gray-500'
							title='Grouped by GSTIN where the party has one, by name where it does not. Sorted by unreconciled value, largest first.'>
							<span>{section === 'purchases' ? 'Vendor' : 'Customer'}</span>
							<Dropdown
								options={vendorOptions}
								value={vendor}
								onChange={setVendor}
								className='w-[22rem]'
							/>
						</div>
					)}
					{!meta.persisted && (
						<Tag tone='amber' title={meta.note}>
							Draft reconciliation — not yet finalized
						</Tag>
					)}
				</div>
			</div>
			{!meta.persisted && <p className='mb-4 text-xs text-amber-700'>{meta.note}</p>}

			<div className='mb-4 flex gap-1 border-b border-gray-200'>
				{(['purchases', 'sales'] as const).map(s => (
					<button
						key={s}
						onClick={() => setSection(s)}
						className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition ${
							section === s
								? 'border-blue-600 text-blue-700'
								: 'border-transparent text-gray-500 hover:text-gray-700'
						}`}>
						{s === 'purchases' ? 'Purchases (2B vs Zoho bills)' : 'Sales (GSTR-1 vs Zoho invoices)'}
					</button>
				))}
			</div>

			{section === 'purchases' ? (
				<GstSide
					side='purchases'
					tabs={summary.purchase_tabs}
					period={period}
					vendor={vendor}
					vendorGstin={vendorGstin}
					rowActions={meta.row_actions ?? []}
					onStatusChange={setActiveStatus}
					onDataChanged={() => setDataVersion(v => v + 1)}
					fetchRows={getGstPurchases}
					exportRows={exportGstPurchases}
					normalize={normalizePurchaseRow as (r: GstPurchaseRow | GstSalesRow, i: number) => NormalizedRow}
					govLabel='GSTR-2B'
					zohoLabel='Zoho Bill'
				/>
			) : (
				<GstSide
					side='sales'
					tabs={summary.sales_tabs}
					period={period}
					vendor={vendor}
					vendorGstin={vendorGstin}
					rowActions={meta.row_actions ?? []}
					onStatusChange={setActiveStatus}
					onDataChanged={() => setDataVersion(v => v + 1)}
					fetchRows={getGstSales}
					exportRows={exportGstSales}
					normalize={normalizeSalesRow as (r: GstPurchaseRow | GstSalesRow, i: number) => NormalizedRow}
					govLabel='GSTR-1'
					zohoLabel='Zoho Invoice'
				/>
			)}
		</section>
	);
};

export default GstReconciliation;
